import type { BoxProps } from '@mui/material';
import { Box, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { CircleAlert, Upload } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import type { Accept, FileRejection } from 'react-dropzone';
import { ErrorCode, useDropzone } from 'react-dropzone';
import * as S from './FileUpload.styles';
import { FileUploadUploadedChips } from './FileUploadUploadedChips';
import type { FileUploadRejectionLabels, FileUploadUploadedItem } from './types';

export interface FileUploadShellProps {
  /** Primary line before the browse affordance (plain text or fragment). */
  uploadLeadingHint: ReactNode;
  /** Inline browse control label. */
  browseLabel: string;
  /** Caption under the primary line (supported formats, etc.). */
  supportedFormatsText?: string;
  disabled?: boolean;
  /** Replaces the cloud icon with a spinner (e.g. while an upload mutation runs). */
  uploading?: boolean;
  /** `data-testid` on the dashed drop root. */
  rootDataTestId?: string;

  /**
   * When set, wires **react-dropzone** on the dashed area (drag-and-drop + browse opens the native picker).
   * When omitted, the zone is non-interactive for files (template preview), unless `onBrowseClick` is set.
   */
  onFilesSelected?: (files: File[]) => void;
  accept?: Accept;
  /** Passed to `useDropzone` (`maxFiles` per drop / browse action). */
  maxFiles?: number;

  /**
   * Copy for a REFUSED pick. See {@link FileUploadRejectionLabels}.
   *
   * `accept` and `maxFiles` do not filter a mistake out of existence — they turn it
   * into a rejection, and a rejection nobody renders is a drop that did nothing under
   * a caption promising the file would be checked. The shell therefore always says
   * something; these labels decide whether it says a sentence or only the file names.
   */
  rejectionLabels?: FileUploadRejectionLabels;

  /**
   * Take the refusal somewhere else — a step-level banner, a form error, a log.
   *
   * Providing it SUPPRESSES the inline message: the caller has claimed the job, and
   * two renderings of one refusal is worse than either alone.
   */
  onFilesRejected?: (rejections: readonly FileRejection[]) => void;

  /**
   * Used when `onFilesSelected` is omitted: whole dashed zone + browse link call this (e.g. story / legacy browse).
   */
  onBrowseClick?: () => void;

  /**
   * Optional block **below** the dashed dropzone (e.g. audit document search).
   * Not part of the drop target.
   */
  librarySection?: ReactNode;

  /**
   * When `false`, suppresses the uploaded-files chip row even if `uploadedFiles` is non-empty.
   * When omitted or `true`, chips render when `uploadedFiles.length > 0`.
   */
  showUploadedFiles?: boolean;
  /** Documents to show **below** the dashed drop zone (before `librarySection`). */
  uploadedFiles?: FileUploadUploadedItem[];
  uploadedFilesLabel?: string;
  onRemoveUploadedFile?: (id: number) => void;
}

/**
 * Dashed document upload shell: drag-and-drop via react-dropzone, browse, optional library row underneath.
 */
export function FileUploadShell({
  uploadLeadingHint,
  browseLabel,
  supportedFormatsText = '',
  disabled = false,
  uploading = false,
  rootDataTestId = 'file-upload-shell',
  onFilesSelected,
  accept,
  maxFiles = 1,
  rejectionLabels,
  onFilesRejected,
  onBrowseClick,
  librarySection,
  showUploadedFiles,
  uploadedFiles = [],
  uploadedFilesLabel,
  onRemoveUploadedFile,
}: FileUploadShellProps) {
  const dropActive = Boolean(onFilesSelected);

  const { getRootProps, getInputProps, isDragActive, isDragReject, open, fileRejections } =
    useDropzone({
      onDrop: (acceptedFiles, rejections) => {
        /* AN EMPTY ACCEPTED LIST IS NOT A SELECTION. `FileUpload` merges this into
           its controlled list, and in single-file mode it merges by REPLACEMENT — so
           a refused drop would clear the file already picked. Every consumer passes
           `multiple` today, where the unguarded call instead re-emits an identical
           list under a fresh array identity; neither outcome is worth a render. */
        if (acceptedFiles.length > 0) onFilesSelected?.(acceptedFiles);
        if (rejections.length > 0) onFilesRejected?.(rejections);
      },
      accept,
      maxFiles,
      disabled: disabled || uploading || !dropActive,
      /* The whole dashed area opens the picker, not just the browse link — the same
         affordance the `browseOnlyZone` branch below already gives the non-drop
         variant. The link keeps its own handler and stops propagation, so a click on
         it opens the dialog once, not twice.

         Keyboard stays off: react-dropzone puts `tabIndex: 0` on the root unless
         `noKeyboard`, which would add a second tab stop in front of the browse link
         that does the identical thing. The link is the keyboard affordance. */
      noClick: false,
      noKeyboard: true,
    });

  /**
   * WHAT THE REFUSED DROP SAYS, or null when there is nothing to say.
   *
   * Read off `fileRejections` rather than held in state on purpose: react-dropzone
   * replaces both halves of that pair on every drop AND every browse — the same
   * `onDropCb` serves the hidden input — so the message clears itself the moment the
   * auditor picks again, and cannot outlive the mistake it describes.
   */
  const rejectionMessage = useMemo<string | null>(() => {
    if (onFilesRejected || fileRejections.length === 0) return null;

    const names = fileRejections.map((rejection) => rejection.file.name);
    const codes = new Set(
      fileRejections.flatMap((rejection) => rejection.errors.map((error) => error.code)),
    );

    /* Ordered by WHAT THE AUDITOR DOES NEXT, not by how many files carry the code.
       Over `maxFiles`, react-dropzone marks EVERY file in the drop too-many-files and
       empties the accepted list — so a first-match-wins on invalid-type would refuse
       perfectly valid PDFs by name and send them off to convert nothing. */
    if (codes.has(ErrorCode.TooManyFiles)) {
      return rejectionLabels?.tooManyFiles?.(maxFiles) ?? names.join(', ');
    }
    if (codes.has(ErrorCode.FileInvalidType)) {
      return rejectionLabels?.invalidType?.(names) ?? names.join(', ');
    }
    if (codes.has(ErrorCode.FileTooLarge) || codes.has(ErrorCode.FileTooSmall)) {
      return rejectionLabels?.invalidSize?.(names) ?? names.join(', ');
    }
    return rejectionLabels?.other?.(names) ?? names.join(', ');
  }, [fileRejections, maxFiles, onFilesRejected, rejectionLabels]);

  const canInteract = !disabled && !uploading;
  const handleBrowse = useCallback(() => {
    if (!canInteract) return;
    if (dropActive) open();
    else onBrowseClick?.();
  }, [canInteract, dropActive, onBrowseClick, open]);

  const browseOnlyZone = !dropActive && Boolean(onBrowseClick);

  let rootSpread: BoxProps = {};
  if (dropActive) {
    rootSpread = getRootProps() as BoxProps;
  } else if (browseOnlyZone) {
    rootSpread = {
      onClick: () => handleBrowse(),
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        if (!canInteract) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleBrowse();
        }
      },
      role: 'button',
      tabIndex: canInteract ? 0 : -1,
      sx: { cursor: canInteract ? 'pointer' : 'default' },
    };
  }

  const interactiveBrowseLink = (dropActive || Boolean(onBrowseClick)) && canInteract;

  const showUploadedChips = showUploadedFiles !== false && uploadedFiles.length > 0;

  const hasLibrarySection = Boolean(librarySection);

  return (
    <Stack spacing={hasLibrarySection ? 0 : 1.5} sx={{ width: '100%' }}>
      <S.FileUploadShellRoot
        isDragActive={dropActive && isDragActive}
        /* `isDragActive` GATES THIS, and the gate is the whole point: react-dropzone
           leaves `isDragReject` true after the drop lands, so an ungated red border
           would stay lit under the message and go on describing a gesture that is
           over. During the drag it is the signal; afterwards the message is. */
        isDragReject={dropActive && isDragActive && isDragReject}
        hasLibrarySection={hasLibrarySection}
        isClickable={dropActive && canInteract}
        {...rootSpread}
        data-testid={rootDataTestId}
      >
        {dropActive ? <input {...getInputProps()} /> : null}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems='center'
          justifyContent='center'
        >
          {uploading ? (
            <CircularProgress size={28} aria-label='uploading' />
          ) : (
            <S.FileUploadIconTile>
              <Upload size={20} strokeWidth={2} aria-hidden />
            </S.FileUploadIconTile>
          )}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            <Typography
              variant='body2'
              color='text.primary'
              component='div'
              fontWeight={600}
              lineHeight={1.4}
            >
              {uploadLeadingHint}{' '}
              {dropActive || onBrowseClick ? (
                <Link
                  component='button'
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    if (interactiveBrowseLink) handleBrowse();
                  }}
                  data-testid='file-upload-browse'
                  color='primary.dark'
                  sx={{
                    verticalAlign: 'baseline',
                    textTransform: 'none',
                    fontWeight: 600,
                    cursor: interactiveBrowseLink ? 'pointer' : 'not-allowed',
                  }}
                  underline='hover'
                  disabled={!interactiveBrowseLink}
                >
                  {browseLabel}
                </Link>
              ) : (
                <Typography component='span' variant='inherit' color='primary.dark'>
                  {browseLabel}
                </Typography>
              )}
            </Typography>
            {supportedFormatsText ? (
              <Typography
                variant='caption'
                color='text.tertiary'
                component='div'
                fontWeight={500}
                lineHeight={1.4}
                sx={{ mt: 0.5, textAlign: { xs: 'center', sm: 'left' } }}
              >
                {supportedFormatsText}
              </Typography>
            ) : null}
            {/*
              IT SITS UNDER THE FORMATS CAPTION because that caption is what the
              refusal contradicts — "PDFs only" and "your XLSX was not added" are one
              thought, and splitting them across the card makes the reader assemble it.

              `role='alert'` rather than colour alone: the refusal follows a gesture
              that produced no other change on screen, so a reader not watching this
              corner gets nothing at all. The icon carries the same meaning the colour
              does, for the reader who cannot use the colour.
            */}
            {rejectionMessage ? (
              <Stack
                direction='row'
                spacing={0.5}
                alignItems='flex-start'
                justifyContent={{ xs: 'center', sm: 'flex-start' }}
                role='alert'
                data-testid='file-upload-rejection'
                sx={{ mt: 0.75, color: 'error.main', textAlign: 'left' }}
              >
                <Box component='span' sx={{ display: 'flex', flexShrink: 0, mt: '1px' }}>
                  <CircleAlert size={14} strokeWidth={2} aria-hidden />
                </Box>
                <Typography
                  component='span'
                  sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.36 }}
                >
                  {rejectionMessage}
                </Typography>
              </Stack>
            ) : null}
          </Box>
        </Stack>
      </S.FileUploadShellRoot>
      {showUploadedChips ? (
        <FileUploadUploadedChips
          label={uploadedFilesLabel}
          items={uploadedFiles}
          onRemove={onRemoveUploadedFile}
        />
      ) : null}
      {librarySection ?? null}
    </Stack>
  );
}
